/// <reference types="vite/client" />

// src/prototype/services/executionPlaybooksService.ts
// GET /api/v2/execution-playbooks/:clientId?portfolio=id1,id2,...

import { dedupeGet, eaCaches, readTtl, writeTtl } from '../utils/sharedTtlCache';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'http://localhost:8080';

export interface ExecutionPlaybookItem {
  index: number;
  use_case_id: number;
  rank: number;
  name: string;
  title: string;
  category_name: string;
  effort_tier: string;
  complexity: string;
  annual_benefit_conservative?: number;
  annual_benefit_aggressive?: number;
  annual_benefit_base?: number;
  estimated_value_low_millions?: number;
  estimated_value_high_millions?: number;
  estimated_value_display: string;
  confidence_percent: number;
  validation_score?: number;
  value_band?: string;
  command_center_path?: string;
}

export interface ExecutionPlaybooksResponse {
  success: boolean;
  client_id: string;
  count: number;
  generated_at?: string;
  meta?: {
    portfolio_filter_provided?: boolean;
    use_case_ids?: number[];
  };
  pagination?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  data: ExecutionPlaybookItem[];
}

export class ExecutionPlaybooksError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ExecutionPlaybooksError';
    this.status = status;
  }
}

export async function fetchExecutionPlaybooks(
  clientId: string,
  portfolioIds: (string | number)[]
): Promise<ExecutionPlaybooksResponse> {
  if (!clientId) {
    throw new ExecutionPlaybooksError('Client ID is required.');
  }

  const portfolio = portfolioIds
    .map(id => String(id).trim())
    .filter(Boolean)
    .join(',');

  const params = portfolio ? `?portfolio=${encodeURIComponent(portfolio)}` : '';
  const url = `${API_BASE}/api/v2/execution-playbooks/${encodeURIComponent(clientId)}${params}`;
  const cached = readTtl<ExecutionPlaybooksResponse>(eaCaches().playbooks, url, 60_000);
  if (cached) return cached;

  return dedupeGet(url, async () => {
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      throw new ExecutionPlaybooksError(
        'Unable to reach the Execution Playbooks service. Check your network connection and ensure the backend is running.'
      );
    }

    if (!res.ok) {
      let detail = '';
      try {
        const errBody = await res.json();
        detail = errBody?.message || errBody?.error || '';
      } catch {
        /* ignore */
      }
      throw new ExecutionPlaybooksError(
        detail || `Execution Playbooks API returned ${res.status}.`,
        res.status
      );
    }

    let body: ExecutionPlaybooksResponse;
    try {
      body = await res.json();
    } catch {
      throw new ExecutionPlaybooksError('Invalid response from Execution Playbooks API.');
    }

    if (!body?.success) {
      throw new ExecutionPlaybooksError('Execution Playbooks API reported failure.');
    }

    if (!Array.isArray(body.data)) {
      throw new ExecutionPlaybooksError('Execution Playbooks response is missing data.');
    }

    writeTtl(eaCaches().playbooks, url, body);
    return body;
  });
}

// ── Execution Kit (expanded playbook detail) ─────────────────────────────────

export interface ExecutionKitScenario {
  annual_benefit?: number;
  annual_benefit_millions?: number;
  annual_benefit_display?: string;
  payback_months?: number;
  payback_display?: string;
  roi_range_low?: number;
  roi_range_high?: number;
  roi_range_low_millions?: number;
  roi_range_high_millions?: number;
  roi_range_display?: string;
  confidence_score_pct?: number;
  confidence_level?: string;
}

export interface ExecutionKitPhase {
  phase: number;
  label: string;
  title: string;
  description: string;
  duration: string;
  months_from_start?: number;
}

export interface ExecutionKitApprovalArtifact {
  id: string;
  title: string;
  subtitle: string;
}

export interface ExecutionKitResponse {
  success: boolean;
  client_id: string;
  use_case_id: number;
  generated_at?: string;
  active_scenario?: string;
  project_info?: {
    use_case_id: number;
    name: string;
    title: string;
    category_name: string;
    status?: string;
    financial_gate_passed?: boolean;
  };
  summary_metrics?: {
    net_value_low?: number;
    net_value_high?: number;
    net_value_low_millions?: number;
    net_value_high_millions?: number;
    net_value_display?: string;
    estimated_value_display?: string;
    confidence_percent?: number;
    confidence_score?: number;
    confidence_level?: string;
  };
  primary_value_driver?: string;
  primary_risk?: string;
  primary_risk_detail?: {
    name: string;
    severity?: string;
    likelihood?: string;
    mitigation?: string;
  };
  execution_rationale?: string;
  confidence_score?: number;
  confidence_level?: string;
  expected_outcomes?: string[];
  dependencies?: { label: string; description: string; severity?: string }[];
  implementation_phases?: ExecutionKitPhase[];
  confidence_snapshot?: {
    confidence_score?: number;
    confidence_score_pct?: number;
    confidence_level?: string;
    primary_value_driver?: string;
    primary_risk?: string;
    scenario?: {
      conservative?: ExecutionKitScenario;
      base?: ExecutionKitScenario;
      aggressive?: ExecutionKitScenario;
    };
  };
  roi_narrative?: string;
  implementation_plan?: ExecutionKitPhase[];
  approval_artifacts?: ExecutionKitApprovalArtifact[];
  financial_model?: {
    view_full_model_path?: string;
    export_csv_path?: string;
  };
  command_center_path?: string;
}

export async function fetchExecutionKit(
  clientId: string,
  useCaseId: string | number
): Promise<ExecutionKitResponse> {
  if (!clientId || useCaseId == null) {
    throw new ExecutionPlaybooksError('Client ID and use case ID are required.');
  }

  const url = `${API_BASE}/api/v2/execution-playbooks/${encodeURIComponent(clientId)}/${encodeURIComponent(String(useCaseId))}/execution-kit`;

  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch {
    throw new ExecutionPlaybooksError(
      'Unable to reach the Execution Kit service. Check your network connection and ensure the backend is running.'
    );
  }

  if (!res.ok) {
    let detail = '';
    try {
      const errBody = await res.json();
      detail = errBody?.message || errBody?.error || '';
    } catch {
      /* ignore */
    }
    throw new ExecutionPlaybooksError(
      detail || `Execution Kit API returned ${res.status}.`,
      res.status
    );
  }

  let body: ExecutionKitResponse;
  try {
    body = await res.json();
  } catch {
    throw new ExecutionPlaybooksError('Invalid response from Execution Kit API.');
  }

  if (!body?.success) {
    throw new ExecutionPlaybooksError('Execution Kit API reported failure.');
  }

  return body;
}
